import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProProfile, getUploadUrl, uploadFileToS3, updatePortfolio, getPortfolio } from "../api";

interface PortfolioItem {
  id: string;
  url: string;
  type: "image" | "video";
  caption: string;
  uploadedAt: string;
}

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionText, setCaptionText] = useState("");
  const [proId, setProId] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const profile = await getProProfile() as any;
        if (profile?.proId) {
          setProId(profile.proId);
          const portfolio = await getPortfolio(profile.proId);
          setItems(portfolio || []);
        }
      } catch (err) {
        console.error("Failed to load portfolio:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    try {
      const newItems: PortfolioItem[] = [];
      for (const file of files) {
        const isVideo = file.type.startsWith("video/");
        const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, "portfolio");
        await uploadFileToS3(uploadUrl, file);
        newItems.push({
          id: Date.now().toString() + Math.random().toString(36).slice(2),
          url: fileUrl,
          type: isVideo ? "video" : "image",
          caption: "",
          uploadedAt: new Date().toISOString()
        });
      }
      const updated = [...items, ...newItems];
      await updatePortfolio(updated);
      setItems(updated);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: string) {
    const updated = items.filter(item => item.id !== id);
    await updatePortfolio(updated);
    setItems(updated);
  }

  async function handleSaveCaption(id: string) {
    const updated = items.map(item =>
      item.id === id ? { ...item, caption: captionText } : item
    );
    await updatePortfolio(updated);
    setItems(updated);
    setEditingCaption(null);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm">
        <button onClick={() => navigate("/home")} className="text-gray-400 text-sm mb-2 block">← Dashboard</button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-navy">Portfolio</h1>
            <p className="text-xs text-gray-400 mt-1">{items.length} items</p>
          </div>
          <label htmlFor="portfolio-upload"
            className="bg-mouna-blue text-white rounded-full px-4 py-2 text-sm font-bold cursor-pointer">
            {uploading ? "Uploading..." : "+ Add"}
          </label>
          <input
            id="portfolio-upload"
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">📸</div>
            <div className="font-bold text-navy text-lg mb-2">No portfolio items yet</div>
            <div className="text-sm text-gray-400 mb-6">Upload photos and videos to showcase your work</div>
            <label htmlFor="portfolio-upload"
              className="bg-mouna-blue text-white rounded-full px-6 py-3 text-sm font-bold cursor-pointer">
              Upload Photos & Videos
            </label>
          </div>
        )}

        {uploading && (
          <div className="bg-blue-pale rounded-2xl p-4 mb-4 flex items-center gap-3">
            <div className="w-5 h-5 border-3 border-mouna-blue border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <div className="text-sm text-mouna-blue font-semibold">Uploading your files...</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="relative aspect-square bg-gray-100">
                {item.type === "video" ? (
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    controls
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.caption || "Portfolio item"}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  {item.type === "video" && (
                    <span className="bg-black bg-opacity-50 text-white text-xs rounded-full px-2 py-0.5">🎥</span>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-2">
                {editingCaption === item.id ? (
                  <div>
                    <input
                      type="text"
                      value={captionText}
                      onChange={e => setCaptionText(e.target.value)}
                      placeholder="Add caption..."
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none mb-1"
                      autoFocus
                    />
                    <div className="flex gap-1">
                      <button onClick={() => setEditingCaption(null)}
                        className="flex-1 text-xs text-gray-400 py-1">Cancel</button>
                      <button onClick={() => handleSaveCaption(item.id)}
                        className="flex-1 text-xs text-mouna-blue font-bold py-1">Save</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingCaption(item.id); setCaptionText(item.caption); }}
                    className="text-xs text-gray-400 w-full text-left"
                  >
                    {item.caption || "Add caption..."}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
